import React, { useContext, useMemo, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FactCheckIcon } from 'components/mui';
import { UserStateContext } from 'context/user-state-context';
import { TaskCreateModal } from '../../../pages/task-manager/task-create-modal.component';

/**
 * CIN-7047: "Create task for customer" from the customer drawer.
 *
 * Same icon and color as the Inbox create-task hover action, so the entry point
 * reads the same across the product. The color comes from the theme's primary
 * (the same #53A6D6 the Inbox action writes out). Renders nothing unless the Task Manager is
 * installed and the user can add tasks.
 *
 * WHY the modal is rendered here instead of through ModalContext.show(): the
 * customer drawer is mounted outside ModalContextProvider (see
 * context/context-providers.js), so show() can't reach it. That is the "custom
 * when show() doesn't fit" case, and details.component.js already renders
 * CallModal this way. TaskCreateModal takes open/onClose/onSubmit explicitly, so
 * nothing else changes.
 *
 * WHY the classic TaskCreateModal even when the taskmanager-updatedUI flag is on:
 * the redesign's create card is still changing, and the boards plan keeps every
 * entry point outside the Task Manager page on the classic modal (plan D15).
 *
 * WHY no "customer_drawer" source type: Summary-tab tasks are 'manual' and
 * Messaging-tab tasks are 'text_message'. A new type would need a choices
 * migration in the boards-owned models for little reporting value (plan D3).
 *
 * The drawer stays open underneath. `onCreated(task)` lets the drawer move to the
 * new task (plan D2).
 *
 * Props:
 *  - customer:    the drawer's customer; prefilled and locked in the modal.
 *  - association: optional source binding, e.g. { source_type: 'text_message' }
 *                 from the Messaging tab. Omitted from the Summary tab, so those
 *                 tasks are 'manual'.
 *  - onCreated:   called with the created task.
 *  - tooltip / size / sx: per-placement wording, size and spacing.
 */
export const DrawerCreateTaskButton = ({
  customer,
  association = null,
  onCreated,
  tooltip = 'Create task for customer',
  size = 'small',
  sx
}) => {
  const {
    hasIntegration,
    hasPermission
  } = useContext(UserStateContext);
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  // The drawer loads its customer from /customers/{id}, which names the last
  // location as `stat_last_location__name`. The create modal labels its prefilled
  // location from `full_location` (the customer list's shape), so pass the name
  // through; otherwise the field reads "Last location". Memoized because the
  // modal resets its form whenever `initialCustomer` changes identity.
  const modalCustomer = useMemo(() => {
    if (!customer || customer.full_location || !customer.stat_last_location__name) return customer;
    return {
      ...customer,
      full_location: {
        name: customer.stat_last_location__name
      }
    };
  }, [customer]);
  if (!customer?.id || !hasIntegration('cinch_task_manager') || !hasPermission('cinch_task_manager.add_taskmanager')) {
    return null;
  }
  return <>
      <Tooltip title={tooltip}>
        <IconButton size={size} onClick={() => setIsOpen(true)} data-testid="drawer-create-task" sx={{
        color: 'primary.main',
        ...sx
      }}>
          <FactCheckIcon fill={theme.palette.primary.main} />
        </IconButton>
      </Tooltip>
      {/* Mounted only while open, so each open starts from a clean form. No board
          is passed: the task lands on the default board and its first stage's
          default deadline applies (boards plan Phase 7). */}
      {isOpen && <TaskCreateModal open onClose={() => setIsOpen(false)} onSubmit={task => {
      setIsOpen(false);
      onCreated?.(task);
    }} initialCustomer={modalCustomer} association={association} lockCustomer />}
    </>;
};